<template>
  <div class="editor">
      <div
        class="menubar"
      >
        <button
          class="menubar__button"
          :disabled="editHTML"
          @click="editor.commands.undo"
        >
          <v-icon small>undo</v-icon>
        </button>

        <button
          class="menubar__button"
          :disabled="editHTML"
          @click="editor.commands.redo"
        >
          <v-icon small>redo</v-icon>
        </button>

          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;

          
        <button
          class="menubar__button"
          :disabled="editHTML"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <v-icon small>format_bold</v-icon>
        </button>

        <button
          class="menubar__button"
          :disabled="editHTML"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <v-icon small>format_italic</v-icon>
        </button>

        <button
          class="menubar__button"
          :disabled="editHTML"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <v-icon small>format_underline</v-icon>
        </button>

        <button
          class="menubar__button"
          :disabled="editHTML"
          :class="{ 'is-active': editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <v-icon small>format_strikethrough</v-icon>
        </button>
        <div 
          style="display:inline"
          v-if="enable_formatting"
        >
          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
          >
            <v-icon small> mdi-format-header-1 </v-icon>
          </button>

          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          >
            <v-icon small> mdi-format-header-2 </v-icon>
          </button>

          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          >
            <v-icon small> mdi-format-header-3 </v-icon>
          </button>

          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            @click="editor.chain().focus().toggleBulletList().run()"
          >
            <v-icon small>format_list_bulleted</v-icon>
          </button>

          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('orderedList') }"
            @click="editor.chain().focus().toggleOrderedList().run()"
          >
            <v-icon small>format_list_numbered</v-icon>
          </button>

          <!-- 
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.blockquote() }"
            @click="editor.commands.blockquote"
          >
            <v-icon small>format_quote</v-icon>
          </button>
          -->

          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('codeBlock') }"
            @click="editor.chain().focus().toggleCodeBlock().run()"
          >
            <v-icon small>code</v-icon>
          </button>

          <button
            class="menubar__button"
            :disabled="editHTML"
            @click="editor.commands.setHorizontalRule()"
          >
            <v-icon small>remove</v-icon>
          </button>

          <button
            class="menubar__button"
            :class="{ 'is-active': editor.isActive('link') }"
            :disabled="editHTML"
            @click="editor.isActive('link') ? editor.commands.unsetLink() : setLink()"
          >
            <v-icon small>{{ editor.isActive('link') ? 'mdi-link-off' : 'link' }}</v-icon>
          </button>
        </div>
        <div
          v-if="enable_html"
          style="display: inline;"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': editHTML }"
            @click="editHTML = !editHTML; $emit('editingHTML', editHTML)"
          > 
            <v-icon small title="Edit with HTML"> mdi-xml </v-icon>
          </button>
        </div>
        <v-menu
          v-if="placeholders"
          offset-y
        >
          <button
            class="menubar__button"
            :disabled="editHTML"
            :class="{ 'is-active': editor.isActive('variablePlaceholder') }"
            style="margin-left:20px;"
            slot="activator"
          >
            <v-icon small>mdi-pound</v-icon>
          </button>
          <v-list>
            <v-subheader>
              Available Placeholders
            </v-subheader>
            <v-list-tile
              v-for="(item, index) in placeholders"
              :key="index"
              dense
              @click="editor.commands.addVariablePlaceholder(item.scope, item.variable)"
            >
              <v-list-tile-title>{{ item.name }}</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </div>

    <editor-content
      v-if="!editHTML"
      :editor="editor"
      class="editor__content"
      :style="height ? 'min-height: ' + height + 'px' : ''"
    />
    <PrismEditor 
      v-if="editHTML"
      class="my-editor"
      language="html" 
      v-model="code" 
      :highlight="highlight" 
      line-numbers
    />
    <div
      v-if="min || max"
      style="text-align:right;padding:5px;"
    >
      <div
        v-if="min && this.getCharCount() < min"
        style="color: red;"
      >
        -{{min - this.getCharCount()}} (min {{min}} char)
      </div>
      <div
        v-else-if="max && this.getCharCount() <= max"
      >
        {{this.getCharCount()}} / {{max}}
      </div>
      <div
        v-else-if="max && this.getCharCount() > max"
      >
        {{this.getCharCount()}} / {{max}}
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import { Node, mergeAttributes } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { PrismEditor } from 'vue-prism-editor'
import 'vue-prism-editor/dist/prismeditor.min.css'
import { highlight, languages } from 'prismjs/components/prism-core'
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css'

const VariablePlaceholder = Node.create({
    name: 'variablePlaceholder',
    group: 'inline',
    inline: true,
    draggable: true,
    addOptions() {
      return {
        ...this.parent,
        variables: []
      }
    },
    addAttributes() {
      return {
        scope: {
          default: null,
          parseHTML: element => element.getAttribute('scope')
        },
        variable: {
          default: null,
          parseHTML: element => element.getAttribute('variable')
        },
      }
    },
    parseHTML () {
      return [
        {
          tag: `span[data-variable-placeholder]`
        }
      ]
    },
    renderHTML({ HTMLAttributes }) {
      var tmp = this.options.variables.filter(item => item['scope'] == HTMLAttributes['scope'] && item['variable'] == HTMLAttributes['variable'])
      var name = "unknown"
      if (tmp.length == 1)
        name = tmp[0]['name']
      return ['span', mergeAttributes(HTMLAttributes, { class: 'placeholder-chip', 'data-variable-placeholder': '' }), ['span', {class: 'mdi mdi-account'}], `${name}`]
    },
    addCommands () {
      return {
        addVariablePlaceholder: (scope, variable) => ({ commands }) => {
          return commands.insertContent([
            {
              type: 'variablePlaceholder',
              attrs: {
                scope: scope,
                variable: variable
              }
            }
          ])
        }
      }
    }
  })

export default {
  name: 'Editor',
  props: [ 'html', 'height', 'enable_formatting', 'placeholders', 'min', 'max', 'enable_html'],
  components: {
    EditorContent,
    PrismEditor
  },
  mounted () {
    this.setHeight()
  },
  data () {
    return {
      editor: new Editor({
        extensions: [
          StarterKit,
          Underline,
          Link,
          VariablePlaceholder.configure({
            variables: this.placeholders
          }),
        ],
        content: this.parsePlaceholder(this.html, this.placeholders),
        autoFocus: true
      }),
      editHTML: false,
      code: ''
    }
  },
  beforeDestroy () {
    this.editor.destroy()
  },
  mounted () {
    this.code = this.parsePlaceholder(this.html, this.placeholders)
    this.editor.on('update', ({editor}) => {
      this.$emit('changed')
    })
  },
  methods: {
    getHTML () {
      if(this.editHTML){
        return this.code
      }
      var html = this.editor.getHTML()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      if (this.placeholders) {
        const spans = doc.querySelectorAll('span[data-variable-placeholder]')
        spans.forEach(span => {
          const scope = span.getAttribute('scope')
          const variable = span.getAttribute('variable')
          span.replaceWith(`{{${scope}.${variable}}}`)
        })
      }

      const serializer = new XMLSerializer()
      return serializer.serializeToString(doc)
    },
    getCharCount () {
      var parser = new DOMParser()
      var doc = parser.parseFromString(this.getHTML(), 'text/html')
      var text = doc.body.textContent || ''
      text = text.replace(/\s+/g, ' ')
      return text.length
    },
    setHeight () {
      document.documentElement.style.setProperty('--min-height-var', this.height + 'px')
    },
    setLink() {
      const url = window.prompt('URL', '')
      this.editor.commands.setLink({ href: url })
    },
    highlight(code) {
      return highlight(code, { 
        ...languages['html']
      }, languages.html)
    }
  }
}
</script>

<style scoped>
  .editor {
    border: 1px solid rgba(0,0,0,0.2);
    padding: 10px;
  }

  .is-active {
    background: #262626!important;
    color:white !important;
  }

  .is-active .v-icon{
    color: white !important;
  }

  .is-empty:first-child::before {
    content: attr(data-empty-text);
    float: left;
    color: #aaa;
    pointer-events: none;
    height: 0;
    font-style: italic;
  }

  button {
    padding:5px;
  }

  .menubar {
    margin-bottom:5px;
  }

  div >>> .ProseMirror {
    min-height: var(--min-height-var);
  }

  div >>> .ProseMirror p {
    margin: 0;
  }

  .ProseMirror:focus-visible {
    outline: none !important;
  }

  .my-editor {
    background: #fff;
    color: #000;
    font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
    font-size: 14px;
    line-height: 1.5;
    padding: 5px;
    height: 300px;
    border: 1px solid rgba(0,0,0,0.2)
  }
</style>

<style>
  .prism-editor__textarea {
    outline: none !important;
  }
</style>
